import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import { useReviewApi } from '@/components/admin/sessions/review/review-context';
import { AnnotatedQuestion, MarkedReply } from '@/components/admin/sessions/review/review-marks';
import { ReviewStamp } from '@/components/admin/sessions/review/review-stamp';
import {
  type ReplySelection,
  type SelectionRead,
  readReviewSelection,
} from '@/lib/review-selection';
import type { ReviewTurn } from '@/lib/review-transcript';
import type { ReviewQuestionNote } from '@matter_extractor/shared';
import { Icon, Tooltip } from '@pms-digital/design-system';
import { type DragEvent, type KeyboardEvent, useRef } from 'react';

/**
 * The transcript, pared down to the words (relecture.md §4.2, D7): the number of
 * the turn, the interviewer's first name and the person's, the text - and
 * nothing of the machine. No step band, no time, no malformed mark, no reread, no
 * objective, no extract of the scribe: a step's name would announce the case it
 * aims at and push one to range by the question asked, the very mistake Patrick
 * named (`scenario-succes.md` §10.3).
 *
 * ## What it reports, and what it does not decide
 *
 * The transcript reads the selection (`readReviewSelection`) and reports it; the
 * screen decides what a passage of a reply or of a question opens. A mark pressed
 * and the « Annoter le message » button report their anchor the same way.
 *
 * The selection is read once the gesture has ended - after a mouse button is
 * released, after a key with Shift, after a touch - never while it is being
 * dragged: a list that opened under a half-made selection would be one more thing
 * to chase.
 */

export interface ReviewTranscriptProps {
  readonly turns: readonly ReviewTurn[];
  readonly interviewer: string;
  readonly person: string;
  /** A selection was made (or refused) in a message. */
  readonly onSelect: (read: SelectionRead) => void;
  /** A selection of a reply is being dragged, or null when the drag ended. */
  readonly onDrag: (read: ReplySelection | null) => void;
  /** A mark was pressed: the extracts under it, and its element. */
  readonly onOpenPlaced: (ids: readonly string[], anchor: HTMLElement) => void;
  /** « Annoter le message » of a turn. */
  readonly onAnnotate: (turn: number, anchor: HTMLElement) => void;
}

function AnnotationCard({ note }: { readonly note: ReviewQuestionNote }) {
  const { apply, canWrite, busy } = useReviewApi();
  const good = note.verdict === 'good';
  return (
    <div
      data-testid={REVIEW_TEST_IDS.annotation}
      data-id={note.id}
      data-verdict={note.verdict}
      className={`grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-2 gap-y-0.5 border-l-[3px] px-2 py-1.5 text-caption ${
        good ? 'border-vert bg-vert-tint' : 'border-rouge-deep bg-rouge-tint'
      }`}
    >
      <span
        className={`whitespace-nowrap font-bold ${good ? 'text-vert-deep' : 'text-rouge-deep'}`}
      >
        {good ? 'Va' : 'Ne va pas'}
      </span>
      <span className="min-w-0 break-words">
        {note.passage === null ? 'le message' : `« ${note.passage.text} »`}
        {note.note === '' ? '' : ` · ${note.note}`}
      </span>
      {canWrite ? (
        <Tooltip content="Retirer l’annotation" side="top">
          <button
            type="button"
            data-testid={REVIEW_TEST_IDS.annotationRemove}
            aria-label="Retirer l’annotation"
            disabled={busy}
            className="inline-flex size-[22px] cursor-pointer items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:border-rouge-deep hover:bg-rouge-tint hover:text-rouge-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed disabled:bg-gray-100"
            onClick={() => void apply([{ op: 'remove-question-note', questionNoteId: note.id }])}
          >
            <Icon name="close" size={14} />
          </button>
        </Tooltip>
      ) : (
        <span />
      )}
      <span className="col-start-2 col-end-4">
        <ReviewStamp stamp={note} />
      </span>
    </div>
  );
}

export function ReviewTranscript({
  turns,
  interviewer,
  person,
  onSelect,
  onDrag,
  onOpenPlaced,
  onAnnotate,
}: ReviewTranscriptProps) {
  const { review, order, canWrite } = useReviewApi();
  const root = useRef<HTMLElement>(null);

  /*
    The selection is final one task after the gesture that ended it: a click
    inside an existing selection collapses it on mouseup, after the event.
  */
  const read = (): void => {
    window.setTimeout(() => {
      const found = readReviewSelection(root.current, window.getSelection());
      if (found !== null) onSelect(found);
    }, 0);
  };

  const onKeyUp = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.shiftKey || event.key === 'Shift') read();
  };

  const onDragStart = (event: DragEvent<HTMLElement>): void => {
    const found = readReviewSelection(root.current, window.getSelection());
    if (found === null || found.kind !== 'reply') return;
    event.dataTransfer.setData('text/plain', found.passage.text);
    event.dataTransfer.effectAllowed = 'copy';
    onDrag(found);
  };

  return (
    <section
      ref={root}
      aria-label="Conversation"
      data-testid={REVIEW_TEST_IDS.transcript}
      onMouseUp={canWrite ? read : undefined}
      onTouchEnd={canWrite ? read : undefined}
      onKeyUp={canWrite ? onKeyUp : undefined}
      onDragStart={canWrite ? onDragStart : undefined}
      onDragEnd={() => onDrag(null)}
      className="flex flex-col gap-0.5"
    >
      {turns.map((turn) => {
        const notes = review.questionNotes.filter((note) => note.turn === turn.number);
        return (
          <div key={turn.number} data-testid={REVIEW_TEST_IDS.turn} data-turn={turn.number}>
            <div className="group grid grid-cols-1 gap-x-3 gap-y-1 rounded-md px-2.5 py-2 sm:grid-cols-[7.5rem_minmax(0,1fr)]">
              <div className="font-semibold text-caption text-gray-600">
                <span className="mr-1.5 font-normal text-gray-400">{turn.number}</span>
                {interviewer}
              </div>
              <div className="flex min-w-0 flex-col gap-1.5">
                <AnnotatedQuestion turn={turn.number} message={turn.message} notes={notes} />
                {notes.length === 0 ? null : (
                  <div className="flex flex-col gap-1.5">
                    {notes.map((note) => (
                      <AnnotationCard key={note.id} note={note} />
                    ))}
                  </div>
                )}
                {canWrite ? (
                  <div>
                    <button
                      type="button"
                      data-testid={REVIEW_TEST_IDS.annotate}
                      data-turn={turn.number}
                      aria-label={`Annoter le message du tour ${turn.number}`}
                      className="cursor-pointer rounded-md border border-gray-200 px-2 text-gray-600 text-caption opacity-60 hover:bg-gray-50 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit group-hover:opacity-100"
                      onClick={(event) => onAnnotate(turn.number, event.currentTarget)}
                    >
                      Annoter le message
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-x-3 gap-y-1 rounded-md bg-gray-50 px-2.5 py-2 sm:grid-cols-[7.5rem_minmax(0,1fr)]">
              <div className="font-semibold text-caption text-gray-600">
                <span className="mr-1.5 font-normal text-gray-400">{turn.number}</span>
                {person}
              </div>
              {turn.reply === null ? (
                <div
                  data-testid={REVIEW_TEST_IDS.noReply}
                  className="text-gray-500 text-small italic leading-[26px]"
                >
                  aucune réponse attendue
                </div>
              ) : (
                <MarkedReply
                  turn={turn.number}
                  reply={turn.reply}
                  extracts={review.extracts.filter((extract) => extract.turn === turn.number)}
                  order={order}
                  onOpen={onOpenPlaced}
                />
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
}
